/* eslint-disable  max-lines */
import { formatISODateTimeZone } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { useSentHomeRequests } from "@src/appV2/Agents/api/useSentHomeRequests";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useOpenNegotiationsCount } from "@src/appV2/Negotiations/api/useOpenNegotiationsCount";
import { useInHomeVisibility } from "@src/appV2/redesign/InHome/lib/useInHomeVisibility";
import { useGetShiftBoosts } from "@src/appV2/redesign/Shift/ShiftBoost/api/useGetShiftBoosts";
import {
  type Bookings,
  InterviewStatus,
  transformResponseIntoInterview,
  useFetchPaginatedInterviews,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useExtraWorkedTimeRequests } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import {
  addDays,
  addHours,
  compareAsc,
  differenceInMinutes,
  isAfter,
  isBefore,
  parseISO,
  setMilliseconds,
  setMinutes,
  setSeconds,
} from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { getVisitsQueryKey } from "../../InHome/useGetVisitsWithCaseData";
import { useWorkerPendingVisitInvites } from "../../InHome/VisitInvites/api/useWorkerPendingVisitInvites";
import { useIsJobInterviewsEnabled } from "../../Placements/useIsJobInterviewsEnabled";
import { CurrentShiftStage } from "../../Shift/constants";
import { getFormattedShiftPayBreakdown } from "../../Shift/getFormattedShiftPayBreakdown";
import { getShiftCurrentStage } from "../../Shift/WorkerShift/getShiftCurrentStage";
import { useGetWorkerShifts } from "../api/useGetWorkerShifts";
import { useGetBookingRequests } from "../BookingRequests/api/useGetBookingRequests";
import { BookingRequestStatus } from "../BookingRequests/types";
import { useWorkerPendingShiftInvites } from "../ShiftInvites/api/useWorkerPendingShiftInvites";
import { useIsSeePastShiftsEnabled } from "../UnverifiedShifts/useIsSeePastShiftsEnabled";
import { useWorkerUnverifiedShifts } from "../UnverifiedShifts/useWorkerUnverifiedShifts";

function isHighlightedBooking(workerTimezone: string, booking?: Bookings): boolean {
  if (!isDefined(booking)) {
    return false;
  }

  if (booking.type === "shift") {
    const shift = booking.data;
    const currentShiftState = getShiftCurrentStage(shift);

    if (currentShiftState !== CurrentShiftStage.NOT_STARTED) {
      return true;
    }

    const now = new Date();
    const next12Hours = addHours(now, 12);
    const bookingStart = parseISO(booking.data.start);
    return isBefore(bookingStart, next12Hours);
  }

  if (booking.type === "interview") {
    const interview = booking.data;
    const nowInWorkerTimezone = utcToZonedTime(new Date(), workerTimezone);
    const next12Hours = addHours(nowInWorkerTimezone, 12);
    return isBefore(interview.start, next12Hours);
  }

  return false;
}

function splitOutHighlightedBooking(bookings: Bookings[], workerTimezone: string) {
  const highlightedBooking =
    bookings.length > 0 && isHighlightedBooking(workerTimezone, bookings[0])
      ? bookings[0]
      : undefined;
  const scheduledBookings = isDefined(highlightedBooking) ? bookings.slice(1) : bookings;
  return {
    highlightedBooking,
    scheduledBookings,
  };
}

// eslint-disable-next-line complexity
export function useWorkerBookingsData() {
  const worker = useDefinedWorker();
  const queryClient = useQueryClient();

  const { isInHomeEnabled } = useInHomeVisibility();
  const isJobInterviewsEnabled = useIsJobInterviewsEnabled();
  const isSeePastShiftsAndPaperTimesheetEnabled = useIsSeePastShiftsEnabled();

  // Fetch shifts
  const {
    shifts,
    isLoading: isLoadingShifts,
    refetch: refetchShifts,
  } = useGetWorkerShifts({ worker });

  // Fetch shift invites
  const {
    data: shiftInvites,
    refetch: refetchShiftInvites,
    isLoading: isLoadingShiftInvites,
  } = useWorkerPendingShiftInvites();

  const {
    data: visitInvitesData,
    isSuccess: isVisitInvitesSuccess,
    refetch: refetchVisitInvites,
    isLoading: isLoadingVisitInvitesQuery,
  } = useWorkerPendingVisitInvites({
    options: {
      enabled: isInHomeEnabled,
    },
  });
  const isLoadingVisitInvites = isInHomeEnabled && isLoadingVisitInvitesQuery;
  const visitInviteCount =
    isInHomeEnabled && isVisitInvitesSuccess ? visitInvitesData.data.length : 0;

  // Fetch unverified shifts
  const {
    data: unverifiedShiftsData,
    refetch: refetchUnverifiedShifts,
    isLoading: isLoadingUnverifiedShifts,
  } = useWorkerUnverifiedShifts();

  // Fetch negotiations
  const {
    data: openNegotiationsCount,
    refetch: refetchNegotiations,
    isLoading: isLoadingNegotiations,
  } = useOpenNegotiationsCount();

  // Fetch sent home requests
  const {
    data: sentHomeRequestsData,
    refetch: refetchSentHome,
    isLoading: isLoadingSentHome,
  } = useSentHomeRequests();

  // Fetch extra worked time requests
  const {
    data: extraWorkedTimeRequestsData,
    refetch: refetchExtraWorked,
    isLoading: isLoadingExtraWorked,
  } = useExtraWorkedTimeRequests();

  const {
    data: bookingRequestsData,
    refetch: refetchBookingRequests,
    isLoading: isLoadingBookingRequests,
  } = useGetBookingRequests(
    {
      filter: { status: { eq: BookingRequestStatus.PENDING }, "worker.id": { eq: worker.userId } },
    },
    { enabled: true }
  );

  // Fetch interviews
  const {
    isLoading: isLoadingInterviews,
    data: interviewsDataWithPages,
    refetch: refetchInterviews,
  } = useFetchPaginatedInterviews(
    {
      filter: {
        status: InterviewStatus.BOOKED,
        start: {
          gt: formatISODateTimeZone(setMilliseconds(setSeconds(setMinutes(new Date(), 0), 0), 0), {
            timeZone: "UTC",
          }),
        },
        end: {
          lt: formatISODateTimeZone(
            setMilliseconds(setSeconds(setMinutes(addDays(new Date(), 30), 0), 0), 0),
            { timeZone: "UTC" }
          ),
        },
      },
    },
    {
      enabled: isDefined(worker.userId) && isJobInterviewsEnabled,
    }
  );

  // Transform interview data
  const interviewsData = interviewsDataWithPages?.pages.flatMap((page) =>
    page.data.map((interview) =>
      transformResponseIntoInterview(interview, page.included ?? [], worker.tmz ?? "")
    )
  );

  // Filter shifts to show only: 1) No sent home requests 2) Not deleted/verified
  const displayableShifts = shifts
    .filter((shift) => !sentHomeRequestsData?.some((request) => request.shift._id === shift._id))
    .filter((shift) => {
      if (!isDefined(shift)) {
        return false;
      }

      // Filter out deleted or verified shifts
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      return !(shift.deleted || shift.verified);
    });

  // Combine shifts and interviews
  const bookings: Bookings[] = [
    ...displayableShifts.map((shift) => ({ type: "shift" as const, data: shift })),
    ...(interviewsData ?? []).map((interview) => ({ type: "interview" as const, data: interview })),
  ].sort((a, b) => {
    const aDate = parseISO(
      a.type === "shift"
        ? a.data.start
        : zonedTimeToUtc(a.data.start, worker.tmz ?? "").toISOString()
    );
    const bDate = parseISO(
      b.type === "shift"
        ? b.data.start
        : zonedTimeToUtc(b.data.start, worker.tmz ?? "").toISOString()
    );
    return compareAsc(aDate, bDate);
  });

  // Split out highlighted booking
  const { highlightedBooking, scheduledBookings } = splitOutHighlightedBooking(
    bookings,
    worker.tmz
  );

  const shiftBoostConfig = useCbhFlag(CbhFeatureFlag.SHIFT_BOOST_CONFIG, {
    defaultValue: { enabled: false },
  });

  // Gets shift boost for highlighted booking only if it is a shift
  const { data: shiftBoosts } = useGetShiftBoosts(
    {
      workerId: worker.userId,
      statuses: ["APPLIED"],
      shiftId: highlightedBooking?.type === "shift" ? highlightedBooking.data._id : undefined,
    },
    {
      enabled:
        shiftBoostConfig.enabled &&
        highlightedBooking?.type === "shift" &&
        isDefined(highlightedBooking.data._id),
    }
  );
  const shiftBoost = shiftBoosts?.data?.[0]?.attributes.amount;

  const startDate =
    typeof highlightedBooking?.data.start === "string"
      ? parseISO(highlightedBooking.data.start)
      : undefined;

  const clockInDate =
    highlightedBooking?.type === "shift" &&
    typeof highlightedBooking.data.clockInOut?.start === "string"
      ? parseISO(highlightedBooking.data.clockInOut.start)
      : undefined;

  const referenceDate = clockInDate ?? new Date();
  const minutesElapsed =
    isDefined(referenceDate) && isDefined(startDate) && isAfter(referenceDate, startDate)
      ? differenceInMinutes(referenceDate, startDate)
      : 0;

  const hourlyPay =
    highlightedBooking?.type === "shift"
      ? (highlightedBooking?.data.offer?.pay.value ?? highlightedBooking?.data.finalPay ?? 0)
      : 0;

  // Calculate shift pay breakdown for highlighted booking
  const highlightedShiftPayBreakdown =
    highlightedBooking?.type === "shift"
      ? getFormattedShiftPayBreakdown(
          highlightedBooking.data.offer?.pay.value,
          highlightedBooking.data.finalPay,
          (highlightedBooking.data.originalAmount ?? 0) - (minutesElapsed / 60) * hourlyPay,
          shiftBoost,
          differenceInMinutes(
            parseISO(highlightedBooking.data.end),
            parseISO(highlightedBooking.data.start)
          ) / 60,
          highlightedBooking.data.facility.requiresLunchBreak
        )
      : undefined;

  const unverifiedShifts = unverifiedShiftsData?.response ?? [];
  const hasUpcomingShifts = bookings.length > 0;

  // Note: interviews are in loading state when isJobInterviewsEnabled is false, so for true loading state both must be checked for true
  const isLoading = isLoadingShifts || (isJobInterviewsEnabled && isLoadingInterviews);
  const showEmptyShiftState =
    !isInHomeEnabled && !isLoading && bookings.length === 0 && !isDefined(highlightedBooking);

  // Function to refresh all data
  async function refreshAllData() {
    await Promise.all([
      refetchShifts(),
      refetchShiftInvites(),
      isInHomeEnabled ? refetchVisitInvites() : Promise.resolve(),
      refetchUnverifiedShifts(),
      refetchNegotiations(),
      refetchSentHome(),
      refetchExtraWorked(),
      isJobInterviewsEnabled ? refetchInterviews() : Promise.resolve(),
      refetchBookingRequests(),
      queryClient.invalidateQueries(getVisitsQueryKey),
    ]);
  }

  return {
    // Booking data
    bookings,
    scheduledBookings,
    highlightedBooking,
    highlightedShiftPayBreakdown,
    hasUpcomingShifts,

    // Loading states
    isLoading,
    isLoadingShiftInvites,
    isLoadingVisitInvites,
    isLoadingUnverifiedShifts,
    isLoadingNegotiations,
    isLoadingSentHome,
    isLoadingExtraWorked,
    isLoadingBookingRequests,

    // Callout data
    shiftInvites,
    visitInviteCount,
    unverifiedShifts,
    openNegotiationsCount,
    sentHomeRequestsData,
    extraWorkedTimeRequestsData,
    bookingRequestsData,

    // Feature flags
    isInHomeEnabled,
    isJobInterviewsEnabled,
    isSeePastShiftsAndPaperTimesheetEnabled,

    // UI state
    showEmptyShiftState,

    // Actions
    refreshAllData,
  };
}

/* eslint-enable  max-lines */
